import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  Loader2,
  Ticket,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { ticketService } from '@/services/ticket.service';
import { useAuthStore } from '@/stores/authStore';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

const statusConfig: Record<string, { icon: typeof Clock; color: string; label: string }> = {
  new: { icon: Clock, color: 'text-blue-600 bg-blue-100', label: 'New' },
  in_progress: { icon: AlertCircle, color: 'text-indigo-600 bg-indigo-100', label: 'In Progress' },
  pending: { icon: Clock, color: 'text-orange-600 bg-orange-100', label: 'Pending' },
  resolved: { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: 'Resolved' },
  closed: { icon: CheckCircle, color: 'text-gray-600 bg-gray-100', label: 'Closed' },
};

export function MyTicketsPage() {
  const { isAuthenticated } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['my-tickets', statusFilter],
    queryFn: () => ticketService.getMyTickets({ status: statusFilter || undefined }),
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) {
    return (
      <div className="py-16">
        <div className="container max-w-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Sign in Required</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please sign in to view your support tickets.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Sign In
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-primary-600">
            Home
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-gray-900 dark:text-white font-medium">My Tickets</span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Support Tickets</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">View and manage your support requests</p>
          </div>
          <Link
            to="/submit-ticket"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Request
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by status:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('')}
                className={`px-3 py-1 rounded-full text-sm ${
                  statusFilter === ''
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                All
              </button>
              {Object.entries(statusConfig).map(([status, config]) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    statusFilter === status
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tickets List */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : tickets?.data && tickets.data.length > 0 ? (
          <div className="space-y-4">
            {tickets.data.map((ticket) => {
              const status = statusConfig[ticket.status] || statusConfig.new;
              const StatusIcon = status.icon;

              return (
                <Link
                  key={ticket.id}
                  to={`/my-tickets/${ticket.id}`}
                  className="block bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-mono text-gray-500 dark:text-gray-400">
                          #{ticket.ticketNumber}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {ticket.subject}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                        {ticket.description}
                      </p>
                    </div>
                    <div className="text-right text-sm text-gray-500 flex-shrink-0 dark:text-gray-400">
                      <div>
                        Created {formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}
                      </div>
                      {ticket.updatedAt !== ticket.createdAt && (
                        <div className="mt-1">
                          Updated {formatDistanceToNow(new Date(ticket.updatedAt), { addSuffix: true })}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* Pagination */}
            {tickets!.totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                {Array.from({ length: tickets!.totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`px-4 py-2 rounded-lg ${
                      page === tickets!.page
                        ? 'bg-primary-600 text-white'
                        : 'bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
            <Ticket className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No tickets found</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {statusFilter
                ? `You don't have any ${statusFilter} tickets.`
                : "You haven't submitted any support requests yet."}
            </p>
            <Link
              to="/submit-ticket"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Submit a Request
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
